/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React from 'react';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useStyles } from './styles';
import {
  CHARACTERS,
  DAYS,
  EXCLUDE,
  EXCLUDE_COMMON_PASSWORDS,
  EXCLUDE_COMMON_PASSWORDS_EXAMPLE,
  EXCLUDE_USER_PROFILE_INFO,
  EXCLUDE_USER_PROFILE_INFO_EXAMPLE,
  EXPIRATION_DATE,
  INCLUDE,
  LENGTH,
  MAX_CONSECUTIVE_LETTERS,
  MAX_OLD_PASSWORDS_STORED,
  MAXIMUM_PASSWORD_LENGTH,
  MINIMUM_PASSWORD_LENGTH,
  MIXED_CASE,
  MIXED_CASE_EXAMPLE,
  NO_EXPIRATION,
  NO_LIMIT,
  NUMBERS,
  NUMBERS_EXAMPLE,
  PASSWORD_CONTENTS,
  PASSWORD_EXPIRES_AFTER,
  PASSWORD_MUST,
  POLICY_NAME,
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_EXAMPLE,
  TIMES
} from '../PasswordPolicyTable/PasswordPolicyDetails/constants';
import { IPasswordPolicyRuleDetail, IDetailList } from '../../../models/PasswordPolicy/PasswordPolicyRuleDetail';

const Heading = ({ label }: { label: string }) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.heading}>
      <Typography variant="body1" gutterBottom component="div" aria-label={label}>
        <Box fontWeight={500}>{label}</Box>
      </Typography>
    </Box>
  );
};

const DetailPolicyMessage = ({ label, value }: { label: string; value: string }) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.valueRow}>
      <Typography variant="body1" gutterBottom component="span" aria-label={`${label} ${value}`}>
        {label}
      </Typography>{' '}
      <Typography
        variant="body1"
        className={classes.value}
        gutterBottom
        component="span"
        aria-label={`${label} ${value}`}
      >
        {value}
      </Typography>
    </Box>
  );
};

const DetailList = ({ titleMessage, option, items }: IDetailList) => {
  const { classes } = useStyles();
  return (
    <>
      <Typography variant="body1" gutterBottom component="span">
        {titleMessage}{' '}
        <Typography component="span" className={classes.accent}>
          {option}
        </Typography>
        :
      </Typography>
      <List className={classes.list}>
        {items.map(({ primary, secondary }: { primary: string; secondary: string }) => (
          <ListItem className={classes.listItem}>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  className={classes.value}
                  gutterBottom
                  component="span"
                  aria-label={primary}
                >
                  {primary}
                </Typography>
              }
              secondary={secondary}
            />
          </ListItem>
        ))}
      </List>
    </>
  );
};

const PasswordPolicyRuleDetail = ({ policyDetails }: IPasswordPolicyRuleDetail) => {
  const { classes } = useStyles();
  const hasIncludeOptions =
    policyDetails.includeNumbers || policyDetails.includeSpecialCharacters || policyDetails.lettersInMixedCase;
  const hasExcludeOptions =
    policyDetails.excludePasswordsInDictionary || policyDetails.excludeUserProfileInfoInPassword;

  return (
    <Box data-testid="password-policy-details">
      <DetailPolicyMessage label={POLICY_NAME} value={policyDetails.name} />

      <Heading label={LENGTH} />

      <DetailPolicyMessage label={MINIMUM_PASSWORD_LENGTH} value={`${policyDetails.minLength} ${CHARACTERS}`} />

      <DetailPolicyMessage label={MAXIMUM_PASSWORD_LENGTH} value={`${policyDetails.maxLength} ${CHARACTERS}`} />

      <Heading label={EXPIRATION_DATE} />

      <DetailPolicyMessage
        label={PASSWORD_EXPIRES_AFTER}
        value={`${policyDetails.expiryDuration ? `${policyDetails.expiryDuration} ${DAYS}` : NO_EXPIRATION}`}
      />

      <Heading label={PASSWORD_CONTENTS} />
      <Box className={classes.valueRow}>
        {hasIncludeOptions && (
          <DetailList
            titleMessage={PASSWORD_MUST}
            option={INCLUDE}
            items={[
              ...(policyDetails.includeNumbers ? [{ primary: NUMBERS, secondary: NUMBERS_EXAMPLE }] : []),
              ...(policyDetails.includeSpecialCharacters
                ? [{ primary: SPECIAL_CHARACTERS, secondary: SPECIAL_CHARACTERS_EXAMPLE }]
                : []),
              ...(policyDetails.lettersInMixedCase ? [{ primary: MIXED_CASE, secondary: MIXED_CASE_EXAMPLE }] : [])
            ]}
          />
        )}

        {hasExcludeOptions && (
          <DetailList
            titleMessage={PASSWORD_MUST}
            option={EXCLUDE}
            items={[
              ...(policyDetails.excludePasswordsInDictionary
                ? [{ primary: EXCLUDE_COMMON_PASSWORDS, secondary: EXCLUDE_COMMON_PASSWORDS_EXAMPLE }]
                : []),
              ...(policyDetails.excludeUserProfileInfoInPassword
                ? [{ primary: EXCLUDE_USER_PROFILE_INFO, secondary: EXCLUDE_USER_PROFILE_INFO_EXAMPLE }]
                : [])
            ]}
          />
        )}
      </Box>

      <DetailPolicyMessage
        label={MAX_CONSECUTIVE_LETTERS}
        value={`${policyDetails.maxConsecutiveLetters ? `${policyDetails.maxConsecutiveLetters} ${TIMES}` : NO_LIMIT}`}
      />

      {policyDetails.passwordHistoryEnabled ? (
        <DetailPolicyMessage
          label={MAX_OLD_PASSWORDS_STORED}
          value={`${policyDetails.oldPasswords ? `${policyDetails.oldPasswords} ` : ''}`}
        />
      ) : null}
    </Box>
  );
};

export default PasswordPolicyRuleDetail;
